import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Edit, Save, X, Send, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './EmailEditor.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailManagement = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTemplate, setTestTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [showButtonDialog, setShowButtonDialog] = useState(false);
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg' or 'html'
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    type: '',
    content: '<p>Hello {{recipient_name}},</p><p>Your email content here...</p>',
    active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Rich text editor configuration with custom button toolbar
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link'],
        ['clean']
      ]
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ];

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Email templates response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Email templates data:', data);
        setTemplates(data);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch templates:', errorData);
        toast.error(`Failed to load templates: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setEditedContent(template.content);
    setEditorMode('wysiwyg'); // Default to WYSIWYG
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: editedContent })
      });

      if (response.ok) {
        toast.success('Email template updated successfully');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        toast.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.type || !newTemplate.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        toast.success('Email template created successfully');
        setShowCreateDialog(false);
        setNewTemplate({
          name: '',
          description: '',
          type: '',
          content: '<p>Hello {{recipient_name}},</p><p>Your email content here...</p>',
          active: true
        });
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error creating template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this email template? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Email template deleted successfully');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    }
  };

  const handleTestClick = (template) => {
    setTestTemplate(template);
    setTestEmail('');
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email-templates/${testTemplate.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipient_email: testEmail })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Test email sent successfully to ${testEmail}`);
        setTestDialogOpen(false);
        setTestEmail('');
      } else {
        toast.error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Error sending test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleInsertButton = () => {
    if (!buttonText || !buttonUrl) {
      toast.error('Please enter both button text and URL');
      return;
    }

    // Create HTML for the button matching the clean design
    const buttonHtml = `
<div class="button-container">
  <a href="${buttonUrl}" class="button">${buttonText}</a>
</div>`;
    
    // Insert button into editor
    setEditedContent(editedContent + buttonHtml);
    
    // Reset and close dialog
    setButtonText('');
    setButtonUrl('');
    setShowButtonDialog(false);
    toast.success('Button inserted');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-teal-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Mail className="w-8 h-8 text-teal-600" />
            Email Management
          </h1>
          <p className="text-gray-600 mt-2">Manage email templates and automated notifications</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Email Templates Yet</h3>
          <p className="text-gray-500 mb-6">Get started by creating your first email template</p>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Template
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Email Templates</h2>
          <p className="text-sm text-gray-600 mb-6">
            Manage and customize your email templates. Changes are saved permanently.
          </p>

          <div className="space-y-4">
          {templates.map(template => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-600">{template.description}</p>
                </div>
                {editingTemplate?.id === template.id ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      size="sm"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                    <Button
                      onClick={() => setEditingTemplate(null)}
                      variant="outline"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleTestClick(template)}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      onClick={() => handleEdit(template)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteTemplate(template.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>

              {editingTemplate?.id === template.id ? (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="template-content">Email Content</Label>
                    <div className="flex gap-2">
                      <div className="flex border border-gray-300 rounded">
                        <button
                          onClick={() => setEditorMode('wysiwyg')}
                          className={`px-3 py-1 text-sm ${editorMode === 'wysiwyg' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                          Rich Text
                        </button>
                        <button
                          onClick={() => setEditorMode('html')}
                          className={`px-3 py-1 text-sm ${editorMode === 'html' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                          HTML
                        </button>
                      </div>
                      <Button
                        onClick={() => setShowButtonDialog(true)}
                        variant="outline"
                        size="sm"
                        className="text-teal-600 border-teal-600 hover:bg-teal-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert Button
                      </Button>
                    </div>
                  </div>
                  
                  {editorMode === 'wysiwyg' ? (
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <ReactQuill
                        theme="snow"
                        value={editedContent}
                        onChange={setEditedContent}
                        modules={modules}
                        formats={formats}
                        className="bg-white"
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                  ) : (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      rows={15}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                      placeholder="Enter HTML content..."
                    />
                  )}
                  
                  <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="font-semibold mb-3 text-blue-900">Available variables (type these in your content):</p>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{recipient_name}}'}</code> <span className="text-gray-600">- Person receiving email</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{creator_name}}'}</code> <span className="text-gray-600">- Person who created task</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{completer_name}}'}</code> <span className="text-gray-600">- Person who completed task</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{updater_name}}'}</code> <span className="text-gray-600">- Person who updated task</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{task_title}}'}</code> <span className="text-gray-600">- Task title</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{task_description}}'}</code> <span className="text-gray-600">- Task description</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{due_date}}'}</code> <span className="text-gray-600">- Task due date</span></div>
                        <div><code className="bg-white px-2 py-1 rounded border border-blue-200 text-blue-900">{'{{notes}}'}</code> <span className="text-gray-600">- Latest notes/comments</span></div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-sm text-gray-700"><strong>Example usage:</strong></p>
                        <p className="text-sm text-gray-600 mt-1">Hi {'{{recipient_name}}'}, {'{{creator_name}}'} has assigned a task to you...</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded p-4 mt-3">
                  <div 
                    className="text-sm text-gray-700 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: template.content }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test version of &quot;{testTemplate?.name}&quot; to verify it looks correct.
              The email will include sample data.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-2">
                The test email will be sent with sample task data to preview the template.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={sendingTest}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={sendingTest}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {sendingTest ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insert Button Dialog */}
      <Dialog open={showButtonDialog} onOpenChange={setShowButtonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Button</DialogTitle>
            <DialogDescription>
              Add a call-to-action button to your email template. The button will use your brand green color.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="button-text">Button Text</Label>
              <Input
                id="button-text"
                type="text"
                placeholder="e.g., View Task"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label htmlFor="button-url">Button URL</Label>
              <Input
                id="button-url"
                type="text"
                placeholder="e.g., {{portal_url}}/profile/tasks"
                value={buttonUrl}
                onChange={(e) => setButtonUrl(e.target.value)}
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-2">
                You can use variables like {'{{portal_url}}'} in the URL
              </p>
            </div>

            {/* Button Preview */}
            {buttonText && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <div style={{ textAlign: 'center' }}>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#0f766e',
                      color: 'white',
                      padding: '12px 30px',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontWeight: 600
                    }}
                  >
                    {buttonText}
                  </a>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowButtonDialog(false);
                setButtonText('');
                setButtonUrl('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInsertButton}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Insert Button
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Email Template</DialogTitle>
            <DialogDescription>
              Create a custom email template. All templates are saved permanently to the database.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="e.g., Welcome Email, Password Reset"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="template-description">Description</Label>
              <Input
                id="template-description"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                placeholder="Brief description of when this email is sent"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="template-type">Template Type *</Label>
              <Input
                id="template-type"
                value={newTemplate.type}
                onChange={(e) => setNewTemplate({...newTemplate, type: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                placeholder="e.g., welcome_email, password_reset (use lowercase and underscores)"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                This is a unique identifier for this template. Use lowercase letters, numbers, and underscores only.
              </p>
            </div>

            <div>
              <Label htmlFor="template-content">Email Content *</Label>
              <div className="mt-2 email-editor-container">
                <ReactQuill
                  value={newTemplate.content}
                  onChange={(content) => setNewTemplate({...newTemplate, content})}
                  modules={modules}
                  formats={formats}
                  theme="snow"
                  className="email-editor"
                />
              </div>
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800 font-medium mb-1">Available Variables:</p>
                <p className="text-xs text-blue-700">
                  Use double curly braces for variables: <code className="bg-blue-100 px-1 rounded">{'{{recipient_name}}'}</code>, 
                  <code className="bg-blue-100 px-1 rounded mx-1">{'{{portal_url}}'}</code>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setNewTemplate({
                  name: '',
                  description: '',
                  type: '',
                  content: '<p>Hello {{recipient_name}},</p><p>Your email content here...</p>',
                  active: true
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              disabled={!newTemplate.name || !newTemplate.type || !newTemplate.content}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
};

export default EmailManagement;
